import Navbar from "@/components/landing/Navbar";
import Footer from "@/components/landing/Footer";

const LAST_UPDATED = "July 8, 2026";

const sections: { title: string; body: React.ReactNode }[] = [
  {
    title: "Overview",
    body: (
      <p>
        Steddy.AI (&ldquo;Steddy&rdquo;, &ldquo;we&rdquo;, &ldquo;us&rdquo;, or &ldquo;our&rdquo;)
        provides software that helps merchant cash advance (MCA) funders and brokers manage
        applications, underwriting, contracts, and related back-office workflows (the
        &ldquo;Service&rdquo;). This Privacy Policy explains what information we collect, how we use
        it, and the choices you have. By using the Service, you agree to the practices described
        here.
      </p>
    ),
  },
  {
    title: "Information We Collect",
    body: (
      <>
        <p>We collect information in the following ways:</p>
        <ul className="list-disc space-y-2 pl-6">
          <li>
            <span className="font-medium text-foreground">Account information</span> you provide
            when you or your organization sign up, such as name, email address, company details, and
            login credentials.
          </li>
          <li>
            <span className="font-medium text-foreground">Application and underwriting data</span>{" "}
            submitted through the Service, including borrower and business information, financial
            statements, bank account data, and supporting documents.
          </li>
          <li>
            <span className="font-medium text-foreground">Connected third-party accounts</span>,
            such as email (Gmail/Outlook) or bank data providers, which you may choose to connect
            via OAuth so the Service can perform actions on your behalf (for example, sending
            contract emails or retrieving bank statements). We only request the minimum scopes
            needed for that functionality.
          </li>
          <li>
            <span className="font-medium text-foreground">Usage data</span> such as log files,
            device and browser information, IP address, and pages or features accessed, which help
            us operate and improve the Service.
          </li>
          <li>
            <span className="font-medium text-foreground">Communications</span> you send us, such as
            support requests or feedback.
          </li>
        </ul>
      </>
    ),
  },
  {
    title: "How We Use Information",
    body: (
      <ul className="list-disc space-y-2 pl-6">
        <li>To provide, operate, and maintain the Service.</li>
        <li>
          To process applications, underwriting decisions, and contract workflows on your behalf.
        </li>
        <li>
          To send transactional communications, such as contract emails, notifications, and account
          updates.
        </li>
        <li>To monitor and improve the security, performance, and reliability of the Service.</li>
        <li>To respond to support requests and communicate with you about your account.</li>
        <li>To comply with legal obligations and enforce our agreements.</li>
      </ul>
    ),
  },
  {
    title: "How We Share Information",
    body: (
      <>
        <p>We do not sell your personal information. We may share information with:</p>
        <ul className="list-disc space-y-2 pl-6">
          <li>
            Service providers who help us operate the Service (for example, hosting, email delivery,
            and data processing vendors), under contractual obligations to protect your information.
          </li>
          <li>
            Third-party integrations you explicitly connect (for example, your email provider or
            bank data provider), solely to perform the actions you authorize.
          </li>
          <li>
            Other parties when required by law, regulation, legal process, or to protect the rights
            and safety of Steddy, our users, or others.
          </li>
          <li>
            A successor entity in connection with a merger, acquisition, or sale of assets, subject
            to standard confidentiality obligations.
          </li>
        </ul>
      </>
    ),
  },
  {
    title: "Data Retention",
    body: (
      <p>
        We retain information for as long as needed to provide the Service and fulfill the purposes
        described in this policy, unless a longer retention period is required or permitted by law.
        When information is no longer needed, we take reasonable steps to delete or anonymize it.
      </p>
    ),
  },
  {
    title: "Security",
    body: (
      <p>
        We use administrative, technical, and physical safeguards designed to protect information
        from unauthorized access, disclosure, alteration, or destruction, including encryption of
        sensitive credentials at rest and in transit. No method of transmission or storage is
        completely secure, and we cannot guarantee absolute security.
      </p>
    ),
  },
  {
    title: "Third-Party OAuth Connections",
    body: (
      <p>
        When you connect a third-party account (such as Gmail or Outlook) to Steddy, we access only
        the scopes necessary for the specific feature you enable (for example, sending contract
        emails from your own mailbox). You can disconnect these accounts at any time from your
        organization settings, after which we will stop using the associated credentials and fall
        back to Steddy&rsquo;s default sending mechanisms where applicable.
      </p>
    ),
  },
  {
    title: "Your Choices",
    body: (
      <p>
        You may access, update, or request deletion of certain account information by contacting us
        or through the Service&rsquo;s settings. You may also disconnect any third-party
        integrations at any time. Depending on your location, you may have additional rights under
        applicable data protection laws.
      </p>
    ),
  },
  {
    title: "Children's Privacy",
    body: (
      <p>
        The Service is intended for business use and is not directed to individuals under the age of
        18. We do not knowingly collect personal information from children.
      </p>
    ),
  },
  {
    title: "Changes to This Policy",
    body: (
      <p>
        We may update this Privacy Policy from time to time. If we make material changes, we will
        update the &ldquo;Last updated&rdquo; date below and, where appropriate, provide additional
        notice.
      </p>
    ),
  },
  {
    title: "Contact Us",
    body: (
      <p>
        If you have questions about this Privacy Policy or our data practices, please contact us at{" "}
        <a href="mailto:privacy@steddy.ai" className="text-primary underline underline-offset-2">
          privacy@steddy.ai
        </a>
        .
      </p>
    ),
  },
];

const PrivacyPolicy = () => (
  <div className="landing-root min-h-screen bg-background">
    <Navbar />
    <main className="mx-auto max-w-3xl px-6 pb-24 pt-32">
      <h1 className="text-4xl font-bold tracking-tight text-foreground">Privacy Policy</h1>
      <p className="mt-3 text-sm text-muted-foreground">Last updated: {LAST_UPDATED}</p>

      <div className="mt-10 space-y-10">
        {sections.map((section) => (
          <section key={section.title}>
            <h2 className="mb-3 text-xl font-semibold text-foreground">{section.title}</h2>
            <div className="space-y-3 text-sm leading-relaxed text-muted-foreground">
              {section.body}
            </div>
          </section>
        ))}
      </div>
    </main>
    <Footer />
  </div>
);

export default PrivacyPolicy;
